import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  GraphNode,
  GraphEdge,
  NodeType
} from "modules/ontology/data/ontology-graph.model";
import "./GraphCanvasOntology.scss";

type GraphCanvasProps = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  selectedNodeId: string | null;
  // In "circular" layout, selectedNodeId also fixes the ego-graph's ring center. Pass this
  // to drive the single-click "selected" ring independently (e.g. drilling into a node
  // inside the overlay shouldn't re-center the ring around it). Defaults to selectedNodeId.
  selectedDetailNodeId?: string | null;
  onNodeSelect: (nodeId: string) => void;
  // Fires on double-click (e.g. to open a "related entities" view for the node).
  onNodeDoubleClick?: (nodeId: string) => void;
  // Node ids to emphasise with a distinct colour (e.g. affected systems).
  highlightedNodeIds?: string[];
  // When true, zoom scales the SVG's pixel size so the container scrolls
  // (instead of the default viewBox zoom which relies on drag-to-pan).
  scrollZoom?: boolean;
  // Shows a mini details card (name/type/connections/status + View/Modify) on node hover,
  // mirroring the table's row actions.
  onNodeView?: (nodeId: string) => void;
  onNodeModify?: (nodeId: string) => void;
  getNodeStatus?: (nodeId: string) => string | undefined;
  // "circular" arranges nodes as a single ring around the focused/selected node (ego graph);
  // defaults to the force-directed layout.
  layout?: "force" | "circular";
};

const HIGHLIGHT_COLOR = "#dc2626";

// Minimum radius every node gets, regardless of graph size; hub nodes grow larger via degree.
const NODE_RADIUS_BASE = 110;

// Splits a label into up to `maxLines` centered lines of at most `maxChars` each.
const wrapLabel = (label: string, maxChars: number, maxLines = 3): string[] => {
  const text = (label ?? "").trim();
  if (!text) return [""];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  const flush = () => {
    if (current) {
      lines.push(current);
      current = "";
    }
  };
  words.forEach((word) => {
    let w = word;
    while (w.length > maxChars) {
      flush();
      lines.push(w.slice(0, maxChars));
      w = w.slice(maxChars);
    }
    if (!current) current = w;
    else if ((current + " " + w).length <= maxChars) current += " " + w;
    else {
      flush();
      current = w;
    }
  });
  flush();
  return lines.slice(0, maxLines);
};

// Average glyph width as a fraction of font-size for the label's sans-serif font —
// used to estimate text width without a canvas measureText call.
const LABEL_CHAR_WIDTH_RATIO = 0.58;

// Picks a font size (shrinking from a preferred size if needed) and wraps the label so
// it's guaranteed to sit inside the node circle's inscribed square, never spilling outside it.
const fitNodeLabel = (
  label: string,
  r: number
): { lines: string[]; fontSize: number; lineHeight: number } => {
  // Inscribed square of the circle, with a small safety margin.
  const box = r * Math.SQRT2 * 0.82;
  let fontSize = Math.max(8, r * 0.32);
  let lines: string[] = [""];
  let lineHeight = fontSize * 1.1;
  for (let attempt = 0; attempt < 6; attempt++) {
    const maxChars = Math.max(
      3,
      Math.floor(box / (fontSize * LABEL_CHAR_WIDTH_RATIO))
    );
    lines = wrapLabel(label, maxChars);
    lineHeight = fontSize * 1.1;
    const fits =
      lines.length * lineHeight <= box &&
      lines.every(
        (line) => line.length * fontSize * LABEL_CHAR_WIDTH_RATIO <= box
      );
    if (fits || fontSize <= 8) break;
    fontSize *= 0.85;
  }
  return { lines, fontSize, lineHeight };
};

// Fruchterman–Reingold force-directed layout for a single connected component
// (dependency-free). Node radii scale with degree so hub nodes get more breathing room,
// and a final collision-resolution pass guarantees no two circles end up overlapping.
const computeForceLayoutForComponent = (
  nodes: GraphNode[],
  edges: GraphEdge[]
): Record<string, { x: number; y: number }> => {
  const positions: Record<string, { x: number; y: number }> = {};
  const n = nodes.length;
  if (n === 0) return positions;
  // Floor the layout area to the node radius itself so small graphs (few nodes, each
  // rendered at NODE_RADIUS_BASE) always get enough room and never overlap/overflow.
  // Generously sized (independent of the now-smaller node radius) so a zoomed-to-fit view
  // naturally renders nodes small with a lot of breathing room between them.
  const width = Math.max(1100, n * 260, NODE_RADIUS_BASE * 10);
  const height = Math.max(800, n * 180, NODE_RADIUS_BASE * 10);
  if (n === 1) {
    positions[nodes[0].id] = { x: width / 2, y: height / 2 };
    return positions;
  }
  const area = width * height;
  const k = Math.sqrt(area / n) * 1.1;

  // Hub nodes (higher degree) get a visibly larger circle, and need more clearance.
  const degree: Record<string, number> = {};
  nodes.forEach((node) => (degree[node.id] = 0));
  edges.forEach((edge) => {
    if (degree[edge.source] !== undefined) degree[edge.source] += 1;
    if (degree[edge.target] !== undefined) degree[edge.target] += 1;
  });
  const nodeRadius: Record<string, number> = {};
  nodes.forEach((node) => {
    nodeRadius[node.id] =
      NODE_RADIUS_BASE + Math.min(degree[node.id] ?? 0, 8) * 5;
  });

  nodes.forEach((node, i) => {
    const angle = (2 * Math.PI * i) / n;
    positions[node.id] = {
      x: width / 2 + k * 0.7 * Math.cos(angle),
      y: height / 2 + k * 0.7 * Math.sin(angle)
    };
  });
  const iterations = 320;
  let temp = width / 8;
  for (let it = 0; it < iterations; it++) {
    const currentTemp = temp;
    const disp: Record<string, { x: number; y: number }> = {};
    nodes.forEach((node) => (disp[node.id] = { x: 0, y: 0 }));
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const na = nodes[a];
        const nb = nodes[b];
        const dx = positions[na.id].x - positions[nb.id].x;
        const dy = positions[na.id].y - positions[nb.id].y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const force = (k * k) / dist;
        const ux = dx / dist;
        const uy = dy / dist;
        disp[na.id].x += ux * force;
        disp[na.id].y += uy * force;
        disp[nb.id].x -= ux * force;
        disp[nb.id].y -= uy * force;
      }
    }
    edges.forEach((edge) => {
      const s = positions[edge.source];
      const t = positions[edge.target];
      if (!s || !t) return;
      const dx = s.x - t.x;
      const dy = s.y - t.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const force = (dist * dist) / k;
      const ux = dx / dist;
      const uy = dy / dist;
      disp[edge.source].x -= ux * force;
      disp[edge.source].y -= uy * force;
      disp[edge.target].x += ux * force;
      disp[edge.target].y += uy * force;
    });
    nodes.forEach((node) => {
      const d = disp[node.id];
      const len = Math.sqrt(d.x * d.x + d.y * d.y) || 0.01;
      positions[node.id].x += (d.x / len) * Math.min(len, currentTemp);
      positions[node.id].y += (d.y / len) * Math.min(len, currentTemp);
      positions[node.id].x += (width / 2 - positions[node.id].x) * 0.008;
      positions[node.id].y += (height / 2 - positions[node.id].y) * 0.008;
    });
    temp *= 0.985;
  }

  // Collision-resolution pass: push apart any two nodes whose circles still overlap
  // (or sit too close), so no node ever visually overlaps another.
  const minGap = 240;
  for (let pass = 0; pass < 80; pass++) {
    let moved = false;
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const na = nodes[a];
        const nb = nodes[b];
        const dx = positions[nb.id].x - positions[na.id].x;
        const dy = positions[nb.id].y - positions[na.id].y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const minDist = nodeRadius[na.id] + nodeRadius[nb.id] + minGap;
        if (dist < minDist) {
          const overlap = (minDist - dist) / 2;
          const ux = dx / dist;
          const uy = dy / dist;
          positions[na.id].x -= ux * overlap;
          positions[na.id].y -= uy * overlap;
          positions[nb.id].x += ux * overlap;
          positions[nb.id].y += uy * overlap;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }

  const pad = 36;
  const xs = nodes.map((node) => positions[node.id].x);
  const ys = nodes.map((node) => positions[node.id].y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  nodes.forEach((node) => {
    positions[node.id].x += pad - minX;
    positions[node.id].y += pad - minY;
  });
  return positions;
};

// Ego-network layout: the focused node (selectedNodeId, or the first node as a fallback)
// sits at the center with every other node evenly spaced around it on a single ring.
const computeCircularLayout = (
  nodes: GraphNode[],
  selectedNodeId: string | null
): Record<string, { x: number; y: number }> => {
  const positions: Record<string, { x: number; y: number }> = {};
  const n = nodes.length;
  if (n === 0) return positions;

  const centerId =
    selectedNodeId && nodes.some((node) => node.id === selectedNodeId)
      ? selectedNodeId
      : nodes[0].id;
  const outerNodes = nodes.filter((node) => node.id !== centerId);
  const ringCount = Math.max(outerNodes.length, 1);
  // Ring radius grows with the node count so circles never overlap each other or the center;
  // the larger multipliers here keep outer nodes well clear of the (bigger) center node.
  const ringRadius = Math.max(
    NODE_RADIUS_BASE * 6,
    (NODE_RADIUS_BASE * 5 * ringCount) / (2 * Math.PI)
  );
  const center = ringRadius + NODE_RADIUS_BASE + 60;

  positions[centerId] = { x: center, y: center };
  outerNodes.forEach((node, i) => {
    const angle = (2 * Math.PI * i) / ringCount - Math.PI / 2;
    positions[node.id] = {
      x: center + ringRadius * Math.cos(angle),
      y: center + ringRadius * Math.sin(angle)
    };
  });
  return positions;
};

// Groups nodes into connected components (union-find), lays each one out independently,
// then packs the components side by side in a grid so disconnected subgraphs render in
// clearly separated regions instead of overlapping/scattering across the same area.
const computeForceLayout = (
  nodes: GraphNode[],
  edges: GraphEdge[]
): Record<string, { x: number; y: number }> => {
  const positions: Record<string, { x: number; y: number }> = {};
  if (nodes.length === 0) return positions;

  const parent = new Map<string, string>();
  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) && parent.get(root) !== root)
      root = parent.get(root)!;
    parent.set(id, root);
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  nodes.forEach((node) => parent.set(node.id, node.id));
  edges.forEach((edge) => {
    if (parent.has(edge.source) && parent.has(edge.target)) {
      union(edge.source, edge.target);
    }
  });

  const groups = new Map<string, GraphNode[]>();
  nodes.forEach((node) => {
    const root = find(node.id);
    (groups.get(root) ?? groups.set(root, []).get(root)!).push(node);
  });

  const nodeIds = new Set(nodes.map((node) => node.id));
  const components = Array.from(groups.values()).map((groupNodes) => {
    const groupIds = new Set(groupNodes.map((node) => node.id));
    const groupEdges = edges.filter(
      (edge) =>
        groupIds.has(edge.source) &&
        groupIds.has(edge.target) &&
        nodeIds.has(edge.source) &&
        nodeIds.has(edge.target)
    );
    const layout = computeForceLayoutForComponent(groupNodes, groupEdges);
    const xs = groupNodes.map((node) => layout[node.id].x);
    const ys = groupNodes.map((node) => layout[node.id].y);
    return {
      nodes: groupNodes,
      layout,
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys)
    };
  });

  // Largest subgraphs placed first so the grid-pack stays compact.
  components.sort((a, b) => b.width * b.height - a.width * a.height);

  const gap = 60;
  const totalArea = components.reduce((sum, c) => sum + c.width * c.height, 0);
  const maxRowWidth = Math.max(1400, Math.sqrt(totalArea) * 1.3);

  let cursorX = 0;
  let cursorY = 0;
  let rowHeight = 0;
  components.forEach((component) => {
    if (cursorX > 0 && cursorX + component.width > maxRowWidth) {
      cursorX = 0;
      cursorY += rowHeight + gap;
      rowHeight = 0;
    }
    component.nodes.forEach((node) => {
      positions[node.id] = {
        x: component.layout[node.id].x + cursorX,
        y: component.layout[node.id].y + cursorY
      };
    });
    cursorX += component.width + gap;
    rowHeight = Math.max(rowHeight, component.height);
  });

  return positions;
};

// Fallback palette for nodeTypes outside the known NodeType enum (e.g. raw ontology
// node types like "Equipment"/"System"); a string hash picks a stable index so every
// node sharing that type always gets the same colour.
const FALLBACK_PALETTE = [
  "#7c3aed",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#3b82f6",
  "#14b8a6",
  "#8b5cf6",
  "#f97316"
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

export const getNodeColor = (type: NodeType): string => {
  switch (type) {
    case NodeType.ASSET:
      return "#7c3aed";
    case NodeType.SYSTEM:
      return "#06b6d4";
    case NodeType.SUBSYSTEM:
      return "#10b981";
    case NodeType.EQUIPMENT:
      return "#f59e0b";
    case NodeType.REQUIREMENT:
      return "#ef4444";
    case NodeType.DISCIPLINE:
      return "#ec4899";
    case NodeType.DOCUMENT:
      return "#3b82f6";
    case NodeType.COMPONENT:
      return "#f59e0b";
    case NodeType.SENSOR:
      return "#06b6d4";
    default:
      return type
        ? FALLBACK_PALETTE[hashString(type) % FALLBACK_PALETTE.length]
        : "#6b7280";
  }
};

const GraphCanvasOntology: FC<GraphCanvasProps> = ({
  nodes,
  edges,
  selectedNodeId,
  selectedDetailNodeId,
  onNodeSelect,
  onNodeDoubleClick,
  highlightedNodeIds,
  scrollZoom,
  onNodeView,
  onNodeModify,
  getNodeStatus,
  layout: layoutMode = "force"
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const highlightSet = useMemo(
    () => new Set(highlightedNodeIds ?? []),
    [highlightedNodeIds]
  );
  const [positions, setPositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const zoomAnchorRef = useRef<{
    fx: number;
    fy: number;
    mx: number;
    my: number;
  } | null>(null);
  const dragRef = useRef<{
    nodeId: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    // Screen-pixel (client) coordinates at mousedown, used only for the drag-vs-click
    // threshold below — SVG-space distance isn't comparable across zoom/layout scales.
    clientStartX: number;
    clientStartY: number;
    moved: boolean;
  } | null>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    origPanX: number;
    origPanY: number;
  } | null>(null);
  // True right after a drag that actually moved the node, so the click event that follows
  // mouseup doesn't also select/deselect the node.
  const didDragRef = useRef(false);

  // Kept as separate memos (rather than one combined useMemo keyed on selectedNodeId) so
  // clicking a node to select it doesn't recompute/replace the force layout's positions —
  // that used to produce a new `layout` object reference on every click, which re-triggered
  // the viewport's auto-recenter effect below and snapped the view away from the clicked node.
  const forceLayoutResult = useMemo(
    () => computeForceLayout(nodes, edges),
    [nodes, edges]
  );
  const circularLayoutResult = useMemo(
    () => computeCircularLayout(nodes, selectedNodeId),
    [nodes, selectedNodeId]
  );
  const layout =
    layoutMode === "circular" ? circularLayoutResult : forceLayoutResult;

  // Larger radius for more connected (hub) nodes; circular (ego) layout renders nodes
  // at half size so the spokes around the focused node stay uncluttered.
  const radiusMap = useMemo(() => {
    const degree: Record<string, number> = {};
    nodes.forEach((node) => (degree[node.id] = 0));
    edges.forEach((edge) => {
      if (degree[edge.source] !== undefined) degree[edge.source] += 1;
      if (degree[edge.target] !== undefined) degree[edge.target] += 1;
    });
    const scale = layoutMode === "circular" ? 0.5 : 1;
    const map: Record<string, number> = {};
    nodes.forEach((node) => {
      map[node.id] =
        (NODE_RADIUS_BASE + Math.min(degree[node.id] ?? 0, 8) * 5) * scale;
    });
    return map;
  }, [nodes, edges, layoutMode]);

  // Edges connecting the same two nodes (in either direction) are grouped so they can be
  // spread apart instead of rendering exactly on top of each other (lines + labels).
  const edgeOffsetIndex = useMemo(() => {
    const groups: Record<string, string[]> = {};
    edges.forEach((edge) => {
      const key = [edge.source, edge.target].sort().join("::");
      (groups[key] ??= []).push(edge.id);
    });
    const index: Record<string, { position: number; count: number }> = {};
    Object.values(groups).forEach((ids) => {
      ids.forEach((id, position) => {
        index[id] = { position, count: ids.length };
      });
    });
    return index;
  }, [edges]);

  // Declutters relationship labels: starts each label at its edge's midpoint, then nudges
  // it away from node circles and from other labels it overlaps so text stays readable.
  // In circular (ego) layout, labels stay fixed to their radial line instead, rotated to
  // run alongside it (matching the spoke-style look of an ego graph).
  const edgeLabelLayout = useMemo(() => {
    const labels: Record<
      string,
      { x: number; y: number; width: number; angle: number; fontSize?: number }
    > = {};
    // Raw (pre-offset) spoke midpoint + direction per edge, kept around so the circular
    // branch below can re-derive each label's final position once its font size is known.
    const circularTemp: Record<
      string,
      { midX: number; midY: number; ux: number; uy: number; textLen: number }
    > = {};
    edges.forEach((edge) => {
      if (!edge.relationship) return;
      const source = positions[edge.source];
      const target = positions[edge.target];
      if (!source || !target) return;
      const sr = radiusMap[edge.source] ?? NODE_RADIUS_BASE;
      const tr = radiusMap[edge.target] ?? NODE_RADIUS_BASE;
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const ux = dx / dist;
      const uy = dy / dist;
      const { position, count } = edgeOffsetIndex[edge.id] ?? {
        position: 0,
        count: 1
      };
      // Spread parallel edges (same node pair) apart in every layout, matching the line
      // render below — otherwise their labels collapse onto the same un-offset midpoint
      // even though the lines themselves are already drawn apart.
      const offset = (position - (count - 1) / 2) * 22;
      const px = -uy * offset;
      const py = ux * offset;
      const x1 = source.x + ux * sr + px;
      const y1 = source.y + uy * sr + py;
      const x2 = target.x - ux * (tr + 6) + px;
      const y2 = target.y - uy * (tr + 6) + py;
      // Keep text upright regardless of which way the line points.
      let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
      if (angle > 90 || angle < -90) angle += 180;
      // In circular (ego) layout the label runs the full length of its spoke, so long
      // relationship names need to shrink to fit that segment instead of spilling onto
      // the nodes at either end.
      let fontSize: number | undefined;
      if (layoutMode === "circular") {
        const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        const baseFontSize = 22;
        const estimatedWidth = edge.relationship.length * baseFontSize * 0.58;
        const available = segmentLength * 0.88;
        fontSize =
          estimatedWidth > available
            ? Math.max(9, (baseFontSize * available) / estimatedWidth)
            : baseFontSize;
      }
      // In circular mode, nudge the label off the spoke to its left side (perpendicular
      // to the line) instead of centering it directly on top of the line.
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      let labelX = midX;
      let labelY = midY - 4;
      if (layoutMode === "circular") {
        const sideOffset = (fontSize ?? 22) * 0.5 + 4;
        labelX = midX + uy * sideOffset;
        labelY = midY - ux * sideOffset;
        circularTemp[edge.id] = {
          midX,
          midY,
          ux,
          uy,
          textLen: edge.relationship.length
        };
      }
      labels[edge.id] = {
        x: labelX,
        y: labelY,
        width: edge.relationship.length * 14 + 24,
        angle,
        fontSize
      };
    });

    // Circular (ego) labels are pinned to their spoke, so instead of nudging them apart
    // (there's nowhere to move them to) each label's font size is capped up front to fit
    // the actual angular gap to its two neighbours around the ring — computed once from
    // real neighbour distances rather than an iterative/capped shrink that can run out of
    // passes before converging on tightly-packed rings.
    if (layoutMode === "circular") {
      const charWidthRatio = 0.58;
      const baseFontSize = 22;
      const minFontSize = 8;
      const labelGap = 10;

      const centerId =
        selectedNodeId && nodes.some((node) => node.id === selectedNodeId)
          ? selectedNodeId
          : nodes[0]?.id;
      const centerPos = centerId ? positions[centerId] : undefined;

      const entries = Object.keys(circularTemp)
        .map((id) => {
          const temp = circularTemp[id];
          const angleFromCenter = centerPos
            ? Math.atan2(temp.midY - centerPos.y, temp.midX - centerPos.x)
            : 0;
          return { id, temp, angleFromCenter };
        })
        .sort((a, b) => a.angleFromCenter - b.angleFromCenter);

      entries.forEach((entry, i) => {
        const { id, temp } = entry;
        const label = labels[id];
        if (entries.length > 1) {
          const prev = entries[(i - 1 + entries.length) % entries.length];
          const next = entries[(i + 1) % entries.length];
          const distToPrev = Math.sqrt(
            (temp.midX - prev.temp.midX) ** 2 +
              (temp.midY - prev.temp.midY) ** 2
          );
          const distToNext = Math.sqrt(
            (temp.midX - next.temp.midX) ** 2 +
              (temp.midY - next.temp.midY) ** 2
          );
          const allowedWidth = Math.min(distToPrev, distToNext) / 2 - labelGap;
          const estimatedWidthAtBase =
            temp.textLen * baseFontSize * charWidthRatio;
          const neighborFit =
            allowedWidth > 0
              ? (baseFontSize * allowedWidth) / estimatedWidthAtBase
              : minFontSize;
          label.fontSize = Math.max(
            minFontSize,
            Math.min(label.fontSize ?? baseFontSize, neighborFit)
          );
        }
        const sideOffset = (label.fontSize ?? baseFontSize) * 0.5 + 4;
        label.x = temp.midX + temp.uy * sideOffset;
        label.y = temp.midY - temp.ux * sideOffset;
      });

      return labels;
    }

    const labelHeight = 28;
    const labelGap = 6;
    const ids = Object.keys(labels);
    for (let pass = 0; pass < 40; pass++) {
      let moved = false;
      nodes.forEach((node) => {
        const pos = positions[node.id];
        if (!pos) return;
        const r = radiusMap[node.id] ?? NODE_RADIUS_BASE;
        ids.forEach((id) => {
          const label = labels[id];
          const dx = label.x - pos.x;
          const dy = label.y - pos.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
          const minDist = r + labelHeight / 2 + labelGap;
          if (dist < minDist) {
            const push = minDist - dist;
            label.x += (dx / dist) * push;
            label.y += (dy / dist) * push;
            moved = true;
          }
        });
      });
      for (let a = 0; a < ids.length; a++) {
        for (let b = a + 1; b < ids.length; b++) {
          const la = labels[ids[a]];
          const lb = labels[ids[b]];
          const dx = lb.x - la.x;
          const dy = lb.y - la.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
          const minDist = (la.width + lb.width) / 2 + labelGap;
          if (dist < minDist) {
            const overlap = (minDist - dist) / 2;
            const ux = dx / dist;
            const uy = dy / dist;
            la.x -= ux * overlap;
            la.y -= uy * overlap;
            lb.x += ux * overlap;
            lb.y += uy * overlap;
            moved = true;
          }
        }
      }
      if (!moved) break;
    }
    return labels;
  }, [
    edges,
    nodes,
    positions,
    radiusMap,
    edgeOffsetIndex,
    layoutMode,
    selectedNodeId
  ]);

  useEffect(() => {
    setPositions(layout);
  }, [layout]);

  // In scroll-zoom mode, track the container size so the graph fits at zoom 1
  // and only overflows (scrolls) when the user zooms in.
  useEffect(() => {
    if (!scrollZoom) {
      return;
    }
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const update = () =>
      setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollZoom]);

  // Keep the graph centred in the viewport on load/resize. While zooming, keep the point
  // under the cursor (or the viewport centre for the zoom buttons) fixed instead.
  useLayoutEffect(() => {
    if (!scrollZoom) {
      return;
    }
    const vp = viewportRef.current;
    if (!vp) {
      return;
    }
    const anchor = zoomAnchorRef.current;
    zoomAnchorRef.current = null;
    if (anchor) {
      vp.scrollLeft = Math.max(0, anchor.fx * vp.scrollWidth - anchor.mx);
      vp.scrollTop = Math.max(0, anchor.fy * vp.scrollHeight - anchor.my);
      return;
    }
    vp.scrollLeft = Math.max(0, (vp.scrollWidth - vp.clientWidth) / 2);
    vp.scrollTop = Math.max(0, (vp.scrollHeight - vp.clientHeight) / 2);
  }, [zoom, containerSize, layout, scrollZoom]);

  const getRadius = (id: string) => radiusMap[id] ?? NODE_RADIUS_BASE;

  const getSVGPoint = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: clientX, y: clientY };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: clientX, y: clientY };
    const svgPt = pt.matrixTransform(ctm.inverse());
    return { x: svgPt.x, y: svgPt.y };
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.preventDefault();
      e.stopPropagation();
      const svgPt = getSVGPoint(e.clientX, e.clientY);
      const pos = positions[nodeId];
      if (!pos) return;
      dragRef.current = {
        nodeId,
        startX: svgPt.x,
        startY: svgPt.y,
        origX: pos.x,
        origY: pos.y,
        clientStartX: e.clientX,
        clientStartY: e.clientY,
        moved: false
      };
    },
    [getSVGPoint, positions]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragRef.current) {
        const svgPt = getSVGPoint(e.clientX, e.clientY);
        const {
          nodeId,
          startX,
          startY,
          origX,
          origY,
          clientStartX,
          clientStartY
        } = dragRef.current;
        const dx = svgPt.x - startX;
        const dy = svgPt.y - startY;
        // Compared in screen pixels (not SVG-space units), which stay meaningful
        // regardless of zoom/layout scale.
        if (
          Math.abs(e.clientX - clientStartX) > 4 ||
          Math.abs(e.clientY - clientStartY) > 4
        )
          dragRef.current.moved = true;
        setPositions((prev) => ({
          ...prev,
          [nodeId]: { x: origX + dx, y: origY + dy }
        }));
      } else if (panRef.current) {
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        const scale = 1 / zoom;
        setPan({
          x: panRef.current.origPanX + dx * scale,
          y: panRef.current.origPanY + dy * scale
        });
      }
    },
    [getSVGPoint, zoom]
  );

  const handleMouseUp = useCallback(() => {
    didDragRef.current = dragRef.current?.moved ?? false;
    dragRef.current = null;
    panRef.current = null;
  }, []);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // In scroll-zoom mode the scrollbars handle navigation, not drag-pan.
      if (scrollZoom) {
        return;
      }
      // Only pan if clicking on the SVG background (not a node)
      if (
        (e.target as Element).tagName === "svg" ||
        (e.target as Element).classList.contains("graph-canvas-ontology__bg")
      ) {
        panRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          origPanX: pan.x,
          origPanY: pan.y
        };
      }
    },
    [pan, scrollZoom]
  );

  // Records which spot of the scrollable content sits at (mx, my) inside the viewport, so
  // the layout effect can restore it after the sizer resizes. Defaults to the viewport centre.
  const captureZoomAnchor = (mx?: number, my?: number) => {
    const vp = viewportRef.current;
    if (!scrollZoom || !vp || !vp.scrollWidth || !vp.scrollHeight) {
      return;
    }
    const anchorX = mx ?? vp.clientWidth / 2;
    const anchorY = my ?? vp.clientHeight / 2;
    zoomAnchorRef.current = {
      fx: (vp.scrollLeft + anchorX) / vp.scrollWidth,
      fy: (vp.scrollTop + anchorY) / vp.scrollHeight,
      mx: anchorX,
      my: anchorY
    };
  };

  const handleZoomIn = () => {
    captureZoomAnchor();
    // Multiplicative step (not additive) so each click feels like a consistent ~30%
    // change regardless of the current zoom level, instead of a huge jump near zoom=1.
    setZoom((z) => Math.min(z * 1.3, 60));
  };
  const handleZoomOut = () => {
    captureZoomAnchor();
    setZoom((z) => Math.max(z / 1.3, 0.2));
  };
  const handleFit = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Ctrl/Cmd + scroll zooms the canvas instead of letting the browser zoom the page;
  // plain scroll (and shift+scroll) keeps scrolling/panning the viewport as before.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const vp = viewportRef.current;
      if (scrollZoom && vp) {
        const rect = vp.getBoundingClientRect();
        captureZoomAnchor(e.clientX - rect.left, e.clientY - rect.top);
      }
      const factor = Math.exp(-e.deltaY * 0.0025);
      setZoom((z) => Math.min(60, Math.max(0.2, z * factor)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollZoom]);

  const getPos = (id: string) => positions[id] || { x: 0, y: 0 };

  // Largest node circle actually present, so padding below always clears every node's rim
  // instead of a fixed guess — otherwise small graphs (few, large nodes) get clipped/overflow.
  const radiusValues = Object.values(radiusMap);
  const maxNodeRadius =
    radiusValues.length > 0 ? Math.max(...radiusValues) : NODE_RADIUS_BASE;

  // Compute dynamic viewBox based on node positions
  const posValues = Object.values(positions);
  const maxX =
    posValues.length > 0
      ? Math.max(...posValues.map((p) => p.x)) + maxNodeRadius + 40
      : 1200;
  const maxY =
    posValues.length > 0
      ? Math.max(...posValues.map((p) => p.y)) + maxNodeRadius + 40
      : 800;
  const vbWidth = Math.max(1200, maxX) / zoom;
  const vbHeight = Math.max(800, maxY) / zoom;
  const contentWidth = Math.max(1200, maxX);
  const contentHeight = Math.max(800, maxY);
  // Scroll mode uses a tight bounding box around the nodes so the graph fills
  // the card (less empty space), then grows a sizer wrapper with zoom.
  const bboxPad = maxNodeRadius + 20;
  const minPx =
    posValues.length > 0 ? Math.min(...posValues.map((p) => p.x)) : 0;
  const minPy =
    posValues.length > 0 ? Math.min(...posValues.map((p) => p.y)) : 0;
  const maxPx =
    posValues.length > 0 ? Math.max(...posValues.map((p) => p.x)) : 1200;
  const maxPy =
    posValues.length > 0 ? Math.max(...posValues.map((p) => p.y)) : 800;
  const bboxW = maxPx - minPx + bboxPad * 2;
  const bboxH = maxPy - minPy + bboxPad * 2;
  const viewBox = scrollZoom
    ? `${minPx - bboxPad} ${minPy - bboxPad} ${bboxW} ${bboxH}`
    : `${-pan.x} ${-pan.y} ${vbWidth} ${vbHeight}`;
  const sizerStyle: React.CSSProperties | undefined = scrollZoom
    ? {
        width: (containerSize.w || contentWidth) * zoom,
        height: (containerSize.h || contentHeight) * zoom
      }
    : undefined;

  return (
    <div className="graph-canvas-ontology" ref={containerRef}>
      <div
        ref={viewportRef}
        className={`graph-canvas-ontology__viewport${
          scrollZoom ? " graph-canvas-ontology__viewport--scroll" : ""
        }`}
      >
        <div
          className={`graph-canvas-ontology__sizer${
            scrollZoom ? " graph-canvas-ontology__sizer--scroll" : ""
          }`}
          style={sizerStyle}
        >
          <svg
            ref={svgRef}
            className="graph-canvas-ontology__svg"
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="5"
                refY="5"
                orient="auto"
              >
                <path d="M0,0 L10,5 L0,10 Z" fill="#6b7280" />
              </marker>
            </defs>

            <g>
              {/* Invisible background for pan detection */}
              <rect
                className="graph-canvas-ontology__bg"
                x={-pan.x}
                y={-pan.y}
                width={5000}
                height={5000}
                fill="transparent"
              />
              {/* Edges */}
              {edges.map((edge) => {
                const source = getPos(edge.source);
                const target = getPos(edge.target);
                const sr = getRadius(edge.source);
                const tr = getRadius(edge.target);
                const dx = target.x - source.x;
                const dy = target.y - source.y;
                const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
                const ux = dx / dist;
                const uy = dy / dist;
                // Spread parallel edges (same node pair) apart perpendicular to the line.
                const { position, count } = edgeOffsetIndex[edge.id] ?? {
                  position: 0,
                  count: 1
                };
                const offset = (position - (count - 1) / 2) * 22;
                const px = -uy * offset;
                const py = ux * offset;
                // Clip line to the circle boundaries; leave room for the arrow.
                const x1 = source.x + ux * sr + px;
                const y1 = source.y + uy * sr + py;
                const x2 = target.x - ux * (tr + 6) + px;
                const y2 = target.y - uy * (tr + 6) + py;
                const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

                return (
                  <g key={edge.id}>
                    <line
                      className="graph-canvas-ontology__edge"
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                    />
                    <polygon
                      className="graph-canvas-ontology__arrow"
                      points="-6,-3.5 0,0 -6,3.5"
                      transform={`translate(${x2}, ${y2}) rotate(${angle})`}
                    />
                  </g>
                );
              })}

              {/* Nodes */}
              {nodes.map((node) => {
                const isHighlighted = highlightSet.has(node.id);
                const color = isHighlighted
                  ? HIGHLIGHT_COLOR
                  : getNodeColor(node.type);
                const isSelected =
                  (selectedDetailNodeId ?? selectedNodeId) === node.id;
                const pos = getPos(node.id);
                const r = getRadius(node.id);
                // Font size and wrapping are derived from the node's actual rendered
                // radius so the label never spills outside the circle.
                const {
                  lines,
                  fontSize: labelFontSize,
                  lineHeight: labelLineHeight
                } = fitNodeLabel(node.label, r);
                const startY = -((lines.length - 1) * labelLineHeight) / 2;

                return (
                  <g
                    key={node.id}
                    className={`graph-node-ontology${
                      isHighlighted ? " graph-node-ontology--highlighted" : ""
                    }`}
                    transform={`translate(${pos.x}, ${pos.y})`}
                    onMouseDown={(e) => handleMouseDown(e, node.id)}
                    onClick={() => {
                      if (didDragRef.current) {
                        didDragRef.current = false;
                        return;
                      }
                      onNodeSelect(node.id);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      onNodeDoubleClick?.(node.id);
                    }}
                  >
                    {isSelected && (
                      <circle
                        className="graph-node-ontology__selection"
                        cx={0}
                        cy={0}
                        r={r + 5}
                      />
                    )}
                    {isHighlighted && (
                      <circle
                        className="graph-node-ontology__highlight-ring"
                        cx={0}
                        cy={0}
                        r={r + 4}
                        fill="none"
                        stroke={HIGHLIGHT_COLOR}
                        strokeWidth={3}
                      />
                    )}
                    <circle
                      className="graph-node-ontology__circle"
                      cx={0}
                      cy={0}
                      r={r}
                      fill={color}
                    />
                    <title>{node.label}</title>
                    <text
                      className="graph-node-ontology__label"
                      textAnchor="middle"
                      style={{ fontSize: labelFontSize }}
                    >
                      {lines.map((line, i) => (
                        <tspan
                          key={i}
                          x={0}
                          y={startY + i * labelLineHeight + 3}
                        >
                          {line}
                        </tspan>
                      ))}
                    </text>
                  </g>
                );
              })}

              {/* Edge labels — rendered after nodes so they're never hidden behind a circle */}
              {edges.map((edge) => {
                if (!edge.relationship) return null;
                const label = edgeLabelLayout[edge.id];
                if (!label) return null;

                return (
                  <g
                    key={`${edge.id}-label`}
                    transform={`rotate(${label.angle} ${label.x} ${label.y})`}
                  >
                    <title>{edge.relationship}</title>
                    {layoutMode !== "circular" && (
                      <rect
                        className="graph-canvas-ontology__edge-label-bg"
                        x={label.x - label.width / 2}
                        y={label.y - 18}
                        width={label.width}
                        height={28}
                        rx={4}
                      />
                    )}
                    <text
                      className="graph-canvas-ontology__edge-label"
                      x={label.x}
                      y={label.y}
                      textAnchor="middle"
                      style={
                        label.fontSize
                          ? { fontSize: label.fontSize }
                          : undefined
                      }
                    >
                      {edge.relationship}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="graph-canvas-ontology__controls">
        <button
          type="button"
          className="graph-canvas-ontology__control-btn"
          onClick={handleZoomOut}
          title="Zoom out"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14" />
          </svg>
        </button>
        <button
          type="button"
          className="graph-canvas-ontology__control-btn"
          onClick={handleZoomIn}
          title="Zoom in"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <button
          type="button"
          className="graph-canvas-ontology__control-btn"
          onClick={handleFit}
          title="Fit to view"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default GraphCanvasOntology;
